<template>
  <div class="hazard-work-table">
    <!-- 섹션 헤더 -->
    <div class="flex items-center mb-4">
      <h5 class="flex items-center"><i class="dot"></i>{{ $t("유해·위험작업") }}</h5>
      <div v-if="!readonly && !isPrint" class="ml-auto flex">
        <vs-button color="secondary" @click="handleAddRow">{{ $t("항목추가") }}</vs-button>
        <vs-button class="ml-2" color="secondary" type="border" :disabled="checkData.length === 0" @click="handleRemoveRow">{{ $t("선택삭제") }}</vs-button>
      </div>
    </div>

    <!-- 데스크탑 / 인쇄: 테이블 -->
    <div :class="{ 'hidden xl:block': !isPrint }" class="table-wrap">
      <table>
        <colgroup>
          <col v-if="!readonly && !isPrint" style="width: 86px" />
          <col style="min-width: 180px" />
          <col style="min-width: 260px" />
          <col style="width: 130px" />
          <col style="width: 340px; min-width: 320px" />
        </colgroup>
        <thead>
          <tr>
            <th v-if="!readonly && !isPrint">
              <vs-checkbox :value="allChecked" class="justify-center mx-0" @click.native="onClickAllCheck" />
            </th>
            <th>{{ $t("장소") }}</th>
            <th>{{ $t("내용") }}</th>
            <th>{{ $t("유해·위험 정도") }}</th>
            <th>{{ $t("사고, 질병의 유형") }}</th>
          </tr>
        </thead>

        <draggable v-if="!readonly && !isPrint" v-model="list" tag="tbody" handle=".drag-handle" :animation="150">
          <tr v-for="row in list" :key="row.id">
            <td class="check-cell">
              <div class="check-cell-inner">
                <vs-checkbox v-model="checkData" :vs-value="row.id" class="mx-0" />
                <feather-icon icon="MenuIcon" class="drag-handle" />
              </div>
            </td>
            <td>
              <ml-input v-model="row.장소" :placeholder="$t('입력필드')" class="w-full" />
            </td>
            <td>
              <ml-input v-model="row.내용" :placeholder="$t('입력필드')" class="w-full" />
            </td>
            <td>
              <vs-select v-model="row.정도" class="w-full type-select">
                <vs-select-item value="적정" :text="$t('적정')" />
                <vs-select-item value="보완" :text="$t('보완')" />
              </vs-select>
            </td>
            <td>
              <multiselect
                v-model="row.사고질병유형"
                :options="hazardGroups"
                :multiple="true"
                :placeholder="$t('(복수선택)')"
                group-values="items"
                group-label="type"
                :group-select="false"
                openDirection="below"
                :close-on-select="false"
                select-label=""
                deselect-label=""
                selected-label="">
                <template #tag="{ option, remove }">
                  <span class="multiselect__tag">
                    <span>{{ $t(typeValue(option)) }}</span>
                    <i tabindex="1" class="multiselect__tag-icon" @click="remove(option)"></i>
                  </span>
                </template>
                <template #option="props">
                  <span v-if="props.option.$groupLabel">{{ $t(props.option.$groupLabel) }}</span>
                  <span v-else>{{ $t(typeValue(props.option)) }}</span>
                </template>
              </multiselect>
              <ml-input v-if="hasAccidentEtc(row)" v-model="row.기타사고" class="mt-2 w-full etc-input" :placeholder="$t('⑮ 기타')" />
              <ml-input v-if="hasIllnessEtc(row)" v-model="row.기타질병" class="mt-2 w-full etc-input" :placeholder="$t('⑤ 기타')" />
            </td>
          </tr>
        </draggable>

        <!-- 읽기/인쇄 전용 -->
        <tbody v-else>
          <tr v-for="row in list" :key="row.id">
            <td>
              <div class="print-input">{{ row.장소 }}</div>
            </td>
            <td>
              <div class="print-input">{{ row.내용 }}</div>
            </td>
            <td>
              <div class="print-input">{{ $t(row.정도) }}</div>
            </td>
            <td>
              <div class="print-input">{{ formatTypes(row.사고질병유형) }}</div>
              <div v-if="hasAccidentEtc(row) && row.기타사고" class="print-input mt-1">{{ row.기타사고 }}</div>
              <div v-if="hasIllnessEtc(row) && row.기타질병" class="print-input mt-1">{{ row.기타질병 }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 모바일: 카드 -->
    <div v-if="!isPrint" class="xl:hidden mobile-view">
      <div class="all-select-row">
        <span class="all-select-title">{{ $t("유해·위험작업") }}</span>
        <label v-if="!readonly" class="all-select-label">
          <span>{{ $t("전체선택") }}</span>
          <vs-checkbox :value="allChecked" class="mx-0" @click.native="onClickAllCheck" />
        </label>
      </div>

      <draggable v-if="!readonly" v-model="list" handle=".drag-handle" :animation="150">
        <div v-for="(row, idx) in list" :key="`m_${row.id}`" class="hazard-card">
          <div class="card-header">
            <span class="index">({{ idx + 1 }})</span>
            <div class="card-header-actions">
              <vs-checkbox v-model="checkData" :vs-value="row.id" class="mx-0" />
              <feather-icon icon="MenuIcon" class="drag-handle" />
            </div>
          </div>
          <div class="card-body">
            <div class="field">
              <label class="field-label">{{ $t("장소") }}</label>
              <ml-input v-model="row.장소" :placeholder="$t('입력필드')" class="field-input" />
            </div>
            <div class="field">
              <label class="field-label">{{ $t("내용") }}</label>
              <ml-input v-model="row.내용" :placeholder="$t('입력필드')" class="field-input" />
            </div>
            <div class="field">
              <label class="field-label">{{ $t("유해·위험 정도") }}</label>
              <vs-select v-model="row.정도" class="field-input type-select" :placeholder="$t('선택')">
                <vs-select-item value="적정" :text="$t('적정')" />
                <vs-select-item value="보완" :text="$t('보완')" />
              </vs-select>
            </div>
            <div class="field field-types">
              <label class="field-label">{{ $t("사고, 질병의 유형") }}</label>
              <div class="field-input">
                <multiselect
                  v-model="row.사고질병유형"
                  :options="hazardGroups"
                  :multiple="true"
                  :placeholder="$t('(복수선택)')"
                  group-values="items"
                  group-label="type"
                  :group-select="false"
                  openDirection="below"
                  :close-on-select="false"
                  select-label=""
                  deselect-label=""
                  selected-label="">
                  <template #tag="{ option, remove }">
                    <span class="multiselect__tag">
                      <span>{{ $t(typeValue(option)) }}</span>
                      <i tabindex="1" class="multiselect__tag-icon" @click="remove(option)"></i>
                    </span>
                  </template>
                  <template #option="props">
                    <span v-if="props.option.$groupLabel">{{ $t(props.option.$groupLabel) }}</span>
                    <span v-else>{{ $t(typeValue(props.option)) }}</span>
                  </template>
                </multiselect>
                <ml-input v-if="hasAccidentEtc(row)" v-model="row.기타사고" class="mt-2 w-full etc-input" :placeholder="$t('⑮ 기타')" />
                <ml-input v-if="hasIllnessEtc(row)" v-model="row.기타질병" class="mt-2 w-full etc-input" :placeholder="$t('⑤ 기타')" />
              </div>
            </div>
          </div>
        </div>
      </draggable>

      <div v-else>
        <div v-for="(row, idx) in list" :key="`mr_${row.id}`" class="hazard-card readonly">
          <div class="card-header">
            <span class="index">({{ idx + 1 }})</span>
          </div>
          <div class="card-body">
            <div class="field">
              <label class="field-label">{{ $t("장소") }}</label>
              <div class="field-input print-input">{{ row.장소 }}</div>
            </div>
            <div class="field">
              <label class="field-label">{{ $t("내용") }}</label>
              <div class="field-input print-input">{{ row.내용 }}</div>
            </div>
            <div class="field">
              <label class="field-label">{{ $t("유해·위험 정도") }}</label>
              <div class="field-input print-input">{{ $t(row.정도) }}</div>
            </div>
            <div class="field">
              <label class="field-label">{{ $t("사고, 질병의 유형") }}</label>
              <div class="field-input">
                <div class="print-input">{{ formatTypes(row.사고질병유형) }}</div>
                <div v-if="hasAccidentEtc(row) && row.기타사고" class="print-input mt-1">{{ row.기타사고 }}</div>
                <div v-if="hasIllnessEtc(row) && row.기타질병" class="print-input mt-1">{{ row.기타질병 }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 안내문 -->
    <ul class="hazard-notes">
      <li>{{ $t("※ 발견한 작업의 내용, 장소 및 유해·위험의 정도를 표시") }}</li>
      <li>{{ $t("※ 파악한 작업의 결과, 발생시의 사고 또는 질병 형태를 표시함") }}</li>
    </ul>

    <!-- 유형 안내(레전드) -->
    <div class="legend-wrap">
      <!-- 사고의 유형 (데스크탑: 5열×3행) -->
      <table class="legend-table accident hidden xl:table">
        <thead>
          <tr>
            <th colspan="5">{{ $t("사고의 유형") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ri) in accidentRowsDesktop" :key="`ad_${ri}`">
            <td v-for="item in row" :key="item">{{ $t(item) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- 사고의 유형 (모바일: 3열×5행) -->
      <table class="legend-table accident xl:hidden">
        <thead>
          <tr>
            <th colspan="3">{{ $t("사고의 유형") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ri) in accidentRowsMobile" :key="`am_${ri}`">
            <td v-for="item in row" :key="item">{{ $t(item) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- 질병의 유형 (공통: 5열×1행) -->
      <table class="legend-table illness">
        <thead>
          <tr>
            <th colspan="5">{{ $t("질병의 유형") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td v-for="item in ILLNESS_TYPES" :key="item">{{ $t(item) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import draggable from "vuedraggable";

const ACCIDENT_TYPES = [
  "① 떨어짐",
  "② 넘어짐",
  "③ 깔림",
  "④ 부딪힘",
  "⑤ 맞음",
  "⑥ 무너짐",
  "⑦ 끼임",
  "⑧ 절단·베임·찔림",
  "⑨ 감전",
  "⑩ 폭발·파열",
  "⑪ 화재",
  "⑫ 불균형 및 무리한 동작",
  "⑬ 이상온도·물체접촉",
  "⑭ 화학물질 누출·접촉",
  "⑮ 기타",
];

const ILLNESS_TYPES = ["① 진폐", "② 중독", "③ 난청", "④ 요통", "⑤ 기타"];

const ACCIDENT_ETC = "⑮ 기타";
const ILLNESS_ETC = "⑤ 기타";

export default {
  name: "HazardWorkTable",
  components: { Multiselect, draggable },
  props: {
    value: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    isPrint: { type: Boolean, default: false },
  },
  data() {
    return {
      list: this.value,
      checkData: [],
      hazardGroups: [
        { type: "사고 유형", items: ACCIDENT_TYPES },
        { type: "질병 유형", items: ILLNESS_TYPES },
      ],
      ACCIDENT_TYPES,
      ILLNESS_TYPES,
    };
  },
  computed: {
    allChecked() {
      return this.list.length > 0 && this.checkData.length === this.list.length;
    },
    accidentRowsDesktop() {
      return this.chunk(ACCIDENT_TYPES, 5);
    },
    accidentRowsMobile() {
      return this.chunk(ACCIDENT_TYPES, 3);
    },
  },
  watch: {
    value: {
      handler(value) {
        this.list = value;
      },
    },
    list: {
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  mounted() {
    if (!this.readonly && !this.isPrint && this.list.length < 1) {
      this.handleAddRow();
    }
  },
  methods: {
    generateId() {
      return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    },
    handleAddRow() {
      this.list.push({
        id: this.generateId(),
        장소: "",
        내용: "",
        정도: "",
        사고질병유형: [],
        기타사고: "",
        기타질병: "",
      });
    },
    handleRemoveRow() {
      this.list = this.list.filter((item) => !this.checkData.includes(item.id));
      this.checkData = [];
    },
    onClickAllCheck() {
      if (this.checkData.length === this.list.length) this.checkData = [];
      else this.checkData = this.list.map((item) => item.id);
    },
    typeValue(item) {
      return typeof item === "string" ? item : (item && (item.label || item.value)) || "";
    },
    hasAccidentEtc(row) {
      return Array.isArray(row.사고질병유형) && row.사고질병유형.some((item) => this.typeValue(item) === ACCIDENT_ETC);
    },
    hasIllnessEtc(row) {
      return Array.isArray(row.사고질병유형) && row.사고질병유형.some((item) => this.typeValue(item) === ILLNESS_ETC);
    },
    formatTypes(types) {
      if (!Array.isArray(types)) return "";
      return types.map((t) => this.$t(this.typeValue(t))).join(", ");
    },
    chunk(arr, size) {
      const out = [];
      for (let i = 0; i < arr.length; i += size) {
        out.push(arr.slice(i, i + size));
      }
      return out;
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
$border-color: #cdcdcd;

.table-wrap {
  width: 100%;
  overflow: visible;

  > table {
    table-layout: fixed;
    border-collapse: collapse;
    width: 100%;

    th,
    td {
      border: 1px solid $border-color;
      padding: 8px;
      vertical-align: middle;

      &::v-deep .con-vs-checkbox {
        justify-content: center !important;
        margin: 0;
        span {
          margin: 0;
        }
      }
    }

    th {
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
      color: #3c3c3c;
      text-align: center;
      background: #f9f9f9;
      padding: 12px 8px;
    }
  }
}

.check-cell {
  background: #f9f9f9;
  vertical-align: middle;

  .check-cell-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .drag-handle {
    width: 16px;
    height: 16px;
    cursor: grab;
    color: #8c8c8c;
    flex-shrink: 0;

    &:active {
      cursor: grabbing;
    }
  }

  &::v-deep .con-vs-checkbox {
    display: inline-flex !important;
    margin: 0 !important;
  }
}

.type-select::v-deep {
  .con-select {
    width: 100%;
  }
}

.etc-input::v-deep input {
  color: #d33b3b;
}

.hazard-notes {
  margin: 24px 0 8px;
  padding: 0;
  list-style: none;

  li {
    font-size: 13px;
    color: #696d71;
    line-height: 20px;
  }
}

.legend-wrap {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  align-items: flex-start;

  .legend-table {
    border-collapse: collapse;
    background: #fff;
    word-break: keep-all;
    overflow-wrap: anywhere;

    th,
    td {
      border: 1px solid $border-color;
      padding: 10px 8px;
      text-align: center;
      vertical-align: middle;
      font-size: 13px;
      color: #3c3c3c;
      line-height: 18px;
    }

    th {
      background: #f9f9f9;
      font-weight: 500;
    }

    /* 데스크탑(accident): 가용 너비 채우면서 5컬럼 균등 분배 */
    &.accident {
      flex: 1 1 0;
      min-width: 0;
      width: 100%;
      table-layout: fixed;
    }

    /* 데스크탑(illness): 콘텐츠 너비만큼만 */
    &.illness {
      flex: 0 0 auto;
      width: auto;
      td {
        padding: 10px 14px;
        white-space: nowrap;
      }
    }
  }
}

/* 모바일: 두 표 모두 100% 너비 세로 스택, 사고의 유형은 3열로 표시(별도 마크업) */
@media (max-width: 1279px) {
  .legend-wrap {
    flex-direction: column;

    .legend-table.accident {
      width: 100%;
      table-layout: fixed;

      /* 2줄 줄바꿈된 셀과 한 줄짜리 셀의 행 높이를 균일하게: 2 × line-height(18) + padding(10×2) = 56 */
      td {
        height: 56px;
      }
    }

    .legend-table.illness {
      flex: 1 1 0;
      width: 100%;
      table-layout: fixed;
      td {
        padding: 10px 8px;
        white-space: normal;
      }
    }
  }
}

/* 모바일 카드 뷰 */
.mobile-view {
  .all-select-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 12px;
    border-top: 1px solid $border-color;
    border-bottom: 1px solid $border-color;
    background: #f9f9f9;

    .all-select-title {
      font-weight: 500;
      font-size: 14px;
      color: #3c3c3c;
    }

    .all-select-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #3c3c3c;
      cursor: pointer;
      margin-bottom: 0;
    }
  }

  .hazard-card {
    border: 1px solid $border-color;
    border-top: none;
    background: #fff;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 16px;
      background: #f9f9f9;
      border-bottom: 1px solid $border-color;

      .index {
        font-weight: 500;
        font-size: 14px;
        color: #3c3c3c;
      }

      .card-header-actions {
        display: flex;
        align-items: center;
        gap: 10px;

        .drag-handle {
          width: 16px;
          height: 16px;
          cursor: grab;
          color: #8c8c8c;

          &:active {
            cursor: grabbing;
          }
        }
      }
    }

    .card-body {
      padding: 16px;

      .field {
        display: flex;
        align-items: flex-start;
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .field-label {
          width: 110px;
          flex-shrink: 0;
          font-size: 14px;
          color: #3c3c3c;
          margin-bottom: 0;
          padding-top: 8px;
        }

        .field-input {
          flex: 1;
          min-width: 0;
        }
      }

      .field-types {
        .field-label {
          padding-top: 8px;
        }
      }
    }

    &.readonly .card-header {
      justify-content: flex-start;
    }
  }
}
</style>

<style lang="scss">
.hazard-work-table {
  .multiselect {
    min-height: 38px;
    font-size: 14px;

    .multiselect__tags {
      min-height: 38px;
      padding: 6px 40px 0 8px;
      border-color: #cdcdcd;

      .multiselect__tag {
        background-color: rgba(var(--vs-secondary), 1) !important;
        margin-right: 4px;
        margin-bottom: 4px;
      }

      .multiselect__tag-icon::after {
        color: rgb(30, 13, 97);
      }

      .multiselect__placeholder {
        color: #b8b8b8;
        padding-top: 2px;
        margin-bottom: 6px;
      }
    }

    .multiselect__option--group {
      background: #f9f9f9 !important;
      color: #3c3c3c !important;
      font-weight: 500;
    }

    .multiselect__content-wrapper {
      z-index: 1000;
      max-height: 240px;
    }

    &.multiselect--active {
      z-index: 1000;
    }
  }
}
</style>
